// client/src/features/documents/expiring.ts — the „expiră în curând" source, named once ·
// DOC-004 (05 DOC-004 criterion 2; 04 §8.10; 03 §15.2).
//
// 05 DOC-004's second acceptance criterion is a SINGLE-SOURCE claim: „expiring-documents view
// feeds from the same data as the calendar chips". MAINT-009 builds the unified compliance
// calendar and has not landed, so this file is the source both sides read rather than a shape
// this screen happens to use. The calendar's documents lane is `expiringDocumentsQueryOptions`
// with its own horizon — not a second call with a second filter, and not a client-side pass over
// the whole archive.
//
// WHAT „EXPIRING" MEANS IS THE SERVER'S. `DocumentStore.Filtered` answers
// `expiringWithinDays=n` with „`expiration_date` is not null and falls on or before
// `IClock.TodayInRomania + n`", which deliberately has no lower bound: everything already
// lapsed comes with it, because the alert ladder repeats after the date and a view that hid an
// expired certificate would be the one place the vault disagreed with the notifications
// (03 §15.2, and the store's own comment). Nothing here re-decides membership.
//
// THE HORIZON is the ladder's widest rung. `DocumentVault.ExpiryLadderDays` is `[60, 30, 14, 7]`
// — the shipped `DOCUMENT_EXPIRY` defaults, which is what `DocumentExpiryScanner` fires on
// (D-986) — so 60 days ahead is exactly the window in which a document has already produced a
// notification. `__tests__/contract-parity.test.ts` reads the C# and fails on a change.
//
// THE COLOUR is `features/equipment/ladder.ts`'s `useComplianceLadder`, asked for the
// `DOCUMENT_EXPIRY` family instead of `REVISION_DUE`: the rungs a landlord actually has are the
// rule row's, per organization, and a screen that coloured from the constant above would outvote
// someone who retuned them or switched the family off (D-942's mistake, one layer up). The
// constant stays the QUERY's default because a URL parameter cannot wait for a fetch, and
// because the rule row is behind `Notifications.ManageRules`, which the `accountant` does not
// hold although they do hold `Documents.Read` (D-1101's wrinkle, met again here).

import { listDocumentsOptions } from '@/client/@tanstack/react-query.gen';
import { daysBetween } from '@/features/equipment/dates';
import { useComplianceLadder, type RevisionLadder } from '@/features/equipment/ladder';

/** 03 §15.2's registry value for the vault's family — `NotificationRuleCatalog.DocumentExpiry`. */
export const DOCUMENT_EXPIRY_RULE_TYPE = 'DOCUMENT_EXPIRY';

/**
 * `DocumentVault.ExpiryHorizonDays` — `ExpiryLadderDays.Max()`, which is 60. How far ahead the
 * expiring view and the compliance calendar look when nothing says otherwise.
 */
export const EXPIRY_HORIZON_DAYS = 60;

/** The rungs the vault ships with — `DocumentVault.ExpiryLadderDays`, widest first. */
export const EXPIRY_LADDER_DAYS = [60, 30, 14, 7] as const;

/**
 * `GET /api/v1/documents?expiringWithinDays=n` — the ONE query the expiring view and MAINT-009's
 * calendar chips both run.
 *
 * Sorted `expirationDate:asc` by the server, so the lapsed rows arrive first and the rest in the
 * order a deadline list is read in. `pageSize` defaults to the 02 §7.4 ceiling because a
 * calendar lane is not paged — it is a window, and the window is what bounds it.
 *
 * @param days how far ahead to look; the caller's horizon, defaulting to the ladder's widest rung.
 * @param pageSize how many rows to take; 200 is §7.4's maximum and the whole window for any
 *   V1-sized portfolio.
 */
export function expiringDocumentsQueryOptions(
  days: number = EXPIRY_HORIZON_DAYS,
  pageSize = 200,
) {
  return listDocumentsOptions({
    query: { expiringWithinDays: days, sort: 'expirationDate:asc', page: 1, pageSize },
  });
}

/** The organization's own document-expiry ladder, for the chips (see the header). */
export function useDocumentExpiryLadder(): RevisionLadder {
  return useComplianceLadder(DOCUMENT_EXPIRY_RULE_TYPE);
}

/**
 * Which side of today a document's expiration falls on — a STRING comparison of two
 * `YYYY-MM-DD` values, which is all the two sections of the expiring view need. `today` is
 * `todayInBucharest()`, the zone every business date in this product is in.
 */
export function hasLapsed(expirationDate: string, today: string): boolean {
  return expirationDate < today;
}

/**
 * Days from today to the expiration; negative once past. Null when either date is malformed.
 *
 * THIS ONE IS COMPUTED HERE, and it is the only figure on these screens that is. Every sibling
 * screen renders a count the server sent — `EquipmentListItem.daysUntilRevision` is
 * `EquipmentStore`'s, decided against `IClock.TodayInRomania`, and D-705 records what it cost to
 * learn that. `DocumentView` has no such field: the vault publishes `expirationDate` and nothing
 * else, so a client that wants to colour a chip by the organization's ladder has to subtract two
 * dates itself. The subtraction is `equipment/dates.ts`'s `daysBetween` — UTC integers, no local
 * calendar, no DST — and `today` comes from Europe/Bucharest rather than the browser's zone, so
 * a landlord in Madrid at 00:30 reads the Bucharest day. Reported as D-1142.
 */
export function daysUntilExpiration(expirationDate: string, today: string): number | null {
  return daysBetween(today, expirationDate);
}
